import React from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import { useI18n, useTranslate } from '../contexts/I18nContext';
import { formatChile } from '../utils/dateUtils';
import { optimizedImageUrl, optimizedSrcSet, eventImageOrDefault, handleEventImageError } from '../utils/imageUrl';
import { isPriceUnknown, PRICE_TBC_LABEL } from '../utils/price';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, MapPin, Star, Users, User, CheckCircle, Sparkles, Building2, Clock, Video } from 'lucide-react';

const DAY_KEYS_CARD = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const getOpenStatusCard = (operatingHours, manualOverrideClosed) => {
  if (manualOverrideClosed) return { isOpen: false };
  if (!operatingHours) return { isOpen: false };
  const now = new Date();
  const chileTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Santiago' }));
  const dayIndex = chileTime.getDay();
  const currentDay = DAY_KEYS_CARD[dayIndex];
  const currentMinutes = chileTime.getHours() * 60 + chileTime.getMinutes();
  
  const todayHours = operatingHours[currentDay];
  if (todayHours) {
    if (todayHours.all_day) return { isOpen: true };
    const [oH, oM] = todayHours.open.split(':').map(Number);
    const [cH, cM] = todayHours.close.split(':').map(Number);
    const openMin = oH * 60 + oM;
    const closeMin = cH * 60 + cM;
    if (closeMin > openMin) {
      if (currentMinutes >= openMin && currentMinutes < closeMin) return { isOpen: true };
    } else {
      if (currentMinutes >= openMin) return { isOpen: true };
    }
  }
  const yesterdayIndex = (dayIndex + 6) % 7;
  const yHours = operatingHours[DAY_KEYS_CARD[yesterdayIndex]];
  if (yHours) {
    const [oH, oM] = yHours.open.split(':').map(Number);
    const [cH, cM] = yHours.close.split(':').map(Number);
    if (cH * 60 + cM <= oH * 60 + oM && currentMinutes < cH * 60 + cM) return { isOpen: true };
  }
  return { isOpen: false };
};

export const EventCard = ({ event, featured = false }) => {
  const { formatPrice } = useCurrency();
  const { t, locale } = useI18n();
  const { translateText } = useTranslate();

  const audienceIcons = {
    kids: Users,
    adults: User,
    family: Users,
  };
  const AudienceIcon = audienceIcons[event.target_audience] || Users;

  return (
    <Card className="group overflow-hidden rounded-3xl border-0 shadow-sm hover:shadow-xl transition-all duration-300 bg-white" data-testid={`event-card-${event.id}`}>
      <Link to={`/event/${event.id}`} className="block">
        {/* Image */}
        <div className="relative overflow-hidden h-48">
          {(() => {
            const cover = eventImageOrDefault(event.main_image);
            const isDefault = !event.main_image;
            return (
              <img
                src={isDefault ? cover : optimizedImageUrl(cover, 400)}
                srcSet={isDefault ? undefined : optimizedSrcSet(cover, [400, 800])}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
                alt={`${event.name} - ${event.category_name || 'Evento'} en ${event.commune_name || 'Chile'}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                style={{ objectPosition: `50% ${event.image_position || '50%'}` }}
                loading="lazy"
                decoding="async"
                width="400"
                height="192"
                onError={handleEventImageError}
              />
            );
          })()}
          
          {/* Price Badge */}
          {event.price !== null && event.price !== undefined ? (
            event.price > 0 ? (
              <div className="absolute top-3 right-3">
                <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-neutral-900 border-0 rounded-full px-3 py-1.5 font-bold shadow-xl ring-1 ring-white/40 inline-flex items-center gap-1">
                  {event.price_is_from && (
                    <span className="text-[10px] font-bold opacity-80 uppercase tracking-wide">
                      {translateText('Desde')}
                    </span>
                  )}
                  <span className="text-sm">{formatPrice(event.price)}</span>
                </Badge>
              </div>
            ) : (
              <div className="absolute top-3 right-3">
                <Badge className="bg-gradient-to-r from-emerald-400 to-[#00CC66] text-white border-0 rounded-full px-3 py-1.5 font-bold shadow-xl ring-1 ring-white/40">
                  {translateText('Gratis')}
                </Badge>
              </div>
            )
          ) : isPriceUnknown(event) ? (
            <div className="absolute top-3 right-3" data-testid={`event-card-price-tbc-${event.id}`}>
              <Badge className="bg-black/55 backdrop-blur text-white border-0 rounded-full px-3 py-1.5 text-xs font-semibold shadow-xl ring-1 ring-white/30">
                {translateText(PRICE_TBC_LABEL)}
              </Badge>
            </div>
          ) : null}

          {/* Featured Badge */}
          {event.is_featured && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-yellow-400 text-yellow-900 border-0 rounded-full px-3 py-1">
                <Sparkles className="w-3 h-3 mr-1" />
                {t('featuredEvents')}
              </Badge>
            </div>
          )}
          {/* Permanent Open/Closed Badge */}
          {event.is_permanent && (
            <div className="absolute bottom-3 left-3 flex gap-1.5">
              <Badge className="bg-blue-500/90 text-white border-0 rounded-full px-2.5 py-0.5 text-xs">
                <Building2 className="w-3 h-3 mr-1" />
                {translateText('Permanente')}
              </Badge>
              {(() => {
                const s = getOpenStatusCard(event.operating_hours, event.manual_override_closed);
                return s.isOpen ? (
                  <Badge className="bg-green-500/90 text-white border-0 rounded-full px-2.5 py-0.5 text-xs" data-testid={`card-open-badge-${event.id}`}>
                    {translateText('Abierto')}
                  </Badge>
                ) : (
                  <Badge className="bg-red-500/90 text-white border-0 rounded-full px-2.5 py-0.5 text-xs" data-testid={`card-closed-badge-${event.id}`}>
                    {translateText('Cerrado')}
                  </Badge>
                );
              })()}
            </div>
          )}
          {/* Online Event Badge */}
          {event.is_online && (
            <div className="absolute bottom-3 left-3">
              <Badge className="bg-violet-500/95 text-white border-0 rounded-full px-2.5 py-0.5 text-xs" data-testid={`card-online-badge-${event.id}`}>
                <Video className="w-3 h-3 mr-1" />
                {translateText('Online')}
              </Badge>
            </div>
          )}
          {/* Recurring badge - never says "Cerrado", shows next session if not active today */}
          {event.is_recurring && !event.is_permanent && (() => {
            const dayLabels = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
            const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Santiago' }));
            const todayIso = now.toISOString().slice(0, 10);

            // DATES MODE: find next upcoming date
            if (event.recurring_mode === 'dates') {
              const dates = (event.recurring_dates || [])
                .filter(d => d && d.date && d.date >= todayIso)
                .sort((a, b) => a.date.localeCompare(b.date));
              if (dates.length === 0) {
                return (
                  <div className="absolute bottom-3 left-3">
                    <Badge className="bg-orange-500/90 text-white border-0 rounded-full px-2.5 py-0.5 text-xs">
                      <Clock className="w-3 h-3 mr-1" />
                      {translateText('Recurrente')}
                    </Badge>
                  </div>
                );
              }
              const next = dates[0];
              const isToday = next.date === todayIso;
              const time = next.all_day ? translateText('Todo el día') : next.open;
              let label;
              if (isToday) {
                label = `${translateText('Hoy')} ${time}`;
              } else {
                // Format day-month label
                const [y, m, day] = next.date.split('-').map(Number);
                const monthShort = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][m - 1] || '';
                label = `${day} ${monthShort} ${time}`;
              }
              return (
                <div className="absolute bottom-3 left-3 flex gap-1.5 flex-wrap max-w-[calc(100%-1.5rem)]">
                  <Badge className="bg-orange-500/90 text-white border-0 rounded-full px-2.5 py-0.5 text-xs" data-testid={`card-recurring-badge-${event.id}`}>
                    <Clock className="w-3 h-3 mr-1" />
                    {label}
                  </Badge>
                </div>
              );
            }

            // WEEKLY MODE
            const todayIdx = now.getDay();
            const hours = event.operating_hours || {};
            // Find next day with hours starting from today
            let nextDay = null;
            for (let i = 0; i < 7; i++) {
              const idx = (todayIdx + i) % 7;
              const h = hours[dayKeys[idx]];
              if (!h) continue;
              if (i === 0 && !h.all_day) {
                // For today, only count if not yet past close
                try {
                  const [oh, om] = h.open.split(':').map(Number);
                  const [ch, cm] = h.close.split(':').map(Number);
                  const cur = now.getHours() * 60 + now.getMinutes();
                  const closeMin = ch * 60 + cm;
                  const openMin = oh * 60 + om;
                  if (closeMin > openMin && cur >= closeMin) continue; // past close today
                } catch { /* ignore */ }
              }
              nextDay = { idx, hours: h, isToday: i === 0 };
              break;
            }
            if (!nextDay) {
              return (
                <div className="absolute bottom-3 left-3">
                  <Badge className="bg-orange-500/90 text-white border-0 rounded-full px-2.5 py-0.5 text-xs">
                    <Clock className="w-3 h-3 mr-1" />
                    {translateText('Recurrente')}
                  </Badge>
                </div>
              );
            }
            const time = nextDay.hours.all_day ? translateText('Todo el día') : nextDay.hours.open;
            const dayLabel = nextDay.isToday ? translateText('Hoy') : dayLabels[nextDay.idx];
            return (
              <div className="absolute bottom-3 left-3 flex gap-1.5 flex-wrap max-w-[calc(100%-1.5rem)]">
                <Badge className="bg-orange-500/90 text-white border-0 rounded-full px-2.5 py-0.5 text-xs" data-testid={`card-recurring-badge-${event.id}`}>
                  <Clock className="w-3 h-3 mr-1" />
                  {nextDay.isToday ? `${translateText('Hoy')} ${time}` : `${dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1)} ${time}`}
                </Badge>
              </div>
            );
          })()}
          {/* Future event badge */}
          {!event.is_permanent && !event.is_recurring && event.start_date && (() => {
            const start = new Date(event.start_date);
            const now = new Date();
            if (start > now) {
              const diff = Math.ceil((start - now) / (1000 * 60 * 60 * 24));
              return (
                <div className="absolute bottom-3 left-3">
                  <Badge className="bg-orange-500/90 text-white border-0 rounded-full px-2.5 py-0.5 text-xs">
                    {diff <= 30 ? translateText(`Faltan ${diff} días`) : translateText('Próximamente')}
                  </Badge>
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Category */}
          {event.category_name && (
            <span className="text-xs font-medium text-[#00CC66] uppercase tracking-wider">
              {translateText(event.category_name)}
            </span>
          )}

          <h3 className="font-bold text-lg text-white mt-1 mb-2 line-clamp-2 group-hover:text-[#00CC66] transition-colors">
            {translateText(event.name)}
          </h3>

          <div className="space-y-2 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              {event.is_permanent ? (
                <>
                  <Clock className="w-4 h-4 text-blue-500 shrink-0" />
                  <span>{translateText('Evento permanente')}</span>
                </>
              ) : event.is_recurring ? (
                <>
                  <Clock className="w-4 h-4 text-orange-500 shrink-0" />
                  <span>{translateText('Evento recurrente')}</span>
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 text-[#00CC66] shrink-0" />
                  <span>
                    {formatChile(event.start_date, "d MMM", locale)}
                    {event.end_date && event.end_date.slice(0, 10) !== event.start_date.slice(0, 10) && (
                      <> - {formatChile(event.end_date, "d MMM", locale)}</>
                    )}
                  </span>
                </>
              )}
            </div>

            {event.is_online ? (
              <div className="flex items-center gap-2">
                <Video className="w-4 h-4 text-violet-500 shrink-0" />
                <span className="truncate">
                  {{
                    zoom: 'Zoom',
                    meet: 'Google Meet',
                    teams: 'Microsoft Teams',
                    youtube: 'YouTube Live',
                    twitch: 'Twitch',
                    instagram: 'Instagram Live',
                    other: translateText('Online')
                  }[event.online_platform] || translateText('Evento online')}
                </span>
              </div>
            ) : event.commune_name && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#00CC66] shrink-0" />
                <span className="truncate">
                  {event.commune_name}
                  {event.region_name && event.region_name !== event.commune_name && (
                    <span className="text-gray-400">, {event.region_name}</span>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Provider info */}
          {event.provider_name && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
              {event.provider_logo_url ? (
                <img
                  src={optimizedImageUrl(event.provider_logo_url, 200)}
                  alt={event.provider_name}
                  className="w-6 h-6 rounded-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width="24"
                  height="24"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-[#064E3B] flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{event.provider_name.charAt(0)}</span>
                </div>
              )}
              <span className="text-xs text-gray-500 truncate">{translateText(event.provider_name)}</span>
              {event.provider_is_verified && (
                <Badge className="bg-green-100 text-green-700 border-0 text-[10px] py-0 px-1.5 rounded-full">
                  <CheckCircle className="w-3 h-3 mr-0.5" />
                  {t('verified')}
                </Badge>
              )}
              {event.provider_is_active_organizer && (
                <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px] py-0 px-1.5 rounded-full">
                  <Sparkles className="w-3 h-3 mr-0.5" />
                  {t('activeOrganizer')}
                </Badge>
              )}
            </div>
          )}
        </div>
      </Link>
    </Card>
  );
};
